import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Save, CheckCircle2, XCircle, Zap, MessageSquare, Info, ChevronRight, Map as MapIcon } from "lucide-react";
import MessagingTemplates from "./MessagingTemplates";
import { invalidateMapProviderCache } from "@/lib/mapProvider";

const inpCls = "font-mono text-sm";

function SecretField({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input value={value || ""} onChange={onChange} placeholder={placeholder} data-testid={testid} className={inpCls} />
    </div>
  );
}

export default function Integrations() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["integrations"], queryFn: () => api.get("/integrations").then((r) => r.data) });

  const save = useMutation({
    mutationFn: ({ group, updates }) => api.patch(`/integrations/${group}`, updates),
    onSuccess: () => { toast.success("Saved securely (encrypted)"); qc.invalidateQueries({ queryKey: ["integrations"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const testConn = useMutation({
    mutationFn: (group) => api.post("/integrations/test", { group }),
    onSuccess: (r) => (r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message)),
  });

  if (isLoading || !data) return <div className="p-8 text-muted-foreground">Loading integrations…</div>;

  return (
    <div className="space-y-4" data-testid="integrations-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Integrations</h1>
        <p className="text-sm text-muted-foreground">Secrets are encrypted at rest and never exposed after save. Update anytime — no restart required.</p>
      </div>

      <Tabs defaultValue="razorpay">
        <TabsList>
          <TabsTrigger value="razorpay" data-testid="tab-razorpay">Razorpay</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms">SMS</TabsTrigger>
          <TabsTrigger value="sms-logs" data-testid="tab-sms-logs">SMS Logs</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email · SMTP</TabsTrigger>
          <TabsTrigger value="maps" data-testid="tab-maps">Maps</TabsTrigger>
        </TabsList>

        <TabsContent value="razorpay" className="mt-4">
          <RazorpayCard cfg={data.razorpay} onSave={(u) => save.mutate({ group: "razorpay", updates: u })} onTest={() => testConn.mutate("razorpay")} saving={save.isPending} testing={testConn.isPending} />
        </TabsContent>
        <TabsContent value="whatsapp" className="mt-4 space-y-4">
          <WhatsAppCard cfg={data.whatsapp} onSave={(u) => save.mutate({ group: "whatsapp", updates: u })} saving={save.isPending} />
          <MessagingTemplates channel="WhatsApp" title="WhatsApp message templates" />
        </TabsContent>
        <TabsContent value="sms" className="mt-4 space-y-4">
          <SMSCard cfg={data.sms} onSave={(u) => save.mutate({ group: "sms", updates: u })} saving={save.isPending} />
          <MessagingTemplates channel="SMS" showComplianceOptIn title="SMS message templates" />
        </TabsContent>
        <TabsContent value="sms-logs" className="mt-4">
          <SMSDispatchLogs />
        </TabsContent>
        <TabsContent value="email" className="mt-4">
          <EmailCard cfg={data.email} onSave={(u) => save.mutate({ group: "email", updates: u })} saving={save.isPending} />
        </TabsContent>
        <TabsContent value="maps" className="mt-4">
          <MapsCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RazorpayCard({ cfg, onSave, onTest, saving, testing }) {
  const [f, setF] = useState({ ...cfg });
  const upd = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><KeyRound size={18} /> Razorpay Payment Gateway</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">Cards, UPI, NetBanking, Wallets · Get keys from <a className="underline" target="_blank" rel="noreferrer" href="https://dashboard.razorpay.com/app/keys">dashboard.razorpay.com</a></div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs">Enable</span>
          <Switch checked={!!f.enabled} onCheckedChange={upd("enabled")} data-testid="rzp-enabled" />
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        <div>
          <Label>Mode</Label>
          <Select value={f.mode || "test"} onValueChange={upd("mode")}>
            <SelectTrigger data-testid="rzp-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="test">Test</SelectItem>
              <SelectItem value="live">Live (production)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <SecretField label="Key ID (rzp_test_... / rzp_live_...)" value={f.key_id} onChange={upd("key_id")} testid="rzp-key-id" placeholder="rzp_test_XXXXXXXXXXXX" />
        <SecretField label="Key Secret" value={f.key_secret} onChange={upd("key_secret")} testid="rzp-key-secret" placeholder="********************" />
        <SecretField label="Webhook Secret (optional)" value={f.webhook_secret} onChange={upd("webhook_secret")} testid="rzp-webhook-secret" placeholder="whsec_...." />
        <div className="col-span-2 flex items-center justify-between border-t border-border/60 pt-3">
          <div className="text-xs text-muted-foreground">
            Webhook URL: <code className="text-primary">{`${window.location.origin.replace('3000','8001')}/api/payments/webhook`}</code>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onTest} disabled={testing} data-testid="rzp-test-btn"><Zap size={14} className="mr-1" />{testing ? "Testing…" : "Test connection"}</Button>
            <Button onClick={() => onSave(f)} disabled={saving} data-testid="rzp-save-btn"><Save size={14} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function WhatsAppCard({ cfg, onSave, saving }) {
  const [f, setF] = useState({ ...cfg });
  const [testPhone, setTestPhone] = useState("");
  const upd = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const updNested = (grp, k) => (e) => setF({ ...f, [grp]: { ...f[grp], [k]: e?.target ? e.target.value : e } });

  const activeProvider = f.active_provider || "meta";
  const providerLabel = {
    meta: "Meta Cloud API",
    interakt: "Interakt",
    gupshup: "Gupshup",
    twilio: "Twilio WhatsApp",
    msg91: "MSG91 WhatsApp",
  }[activeProvider];

  const testWa = useMutation({
    mutationFn: () => api.post("/integrations/test", { group: "whatsapp", phone: testPhone }),
    onSuccess: (r) => (r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message)),
    onError: (e) => toast.error(e.response?.data?.detail || "Test failed"),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle>WhatsApp Business Gateway</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">Multi-provider — pick one active. Send order updates, invoices, payment links.</div>
        </div>
        <div className="flex items-center gap-2"><span className="text-xs">Enable</span><Switch checked={!!f.enabled} onCheckedChange={upd("enabled")} data-testid="wa-enabled" /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs">
          <Label>Active provider</Label>
          <Select value={activeProvider} onValueChange={upd("active_provider")}>
            <SelectTrigger data-testid="wa-provider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="meta">Meta Cloud API (Official)</SelectItem>
              <SelectItem value="interakt">Interakt</SelectItem>
              <SelectItem value="gupshup">Gupshup</SelectItem>
              <SelectItem value="twilio">Twilio WhatsApp</SelectItem>
              <SelectItem value="msg91">MSG91 WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {activeProvider === "meta" && (
          <div className="rounded border border-border/50 p-3 space-y-3" data-testid="wa-meta-fields">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Meta Cloud API</div>
            <div className="grid grid-cols-2 gap-3">
              <SecretField label="Access Token" value={f.meta?.access_token} onChange={updNested("meta", "access_token")} testid="wa-meta-token" placeholder="EAA..." />
              <SecretField label="Phone Number ID" value={f.meta?.phone_number_id} onChange={updNested("meta", "phone_number_id")} testid="wa-meta-phoneid" />
              <SecretField label="WABA ID" value={f.meta?.waba_id} onChange={updNested("meta", "waba_id")} testid="wa-meta-waba" />
              <SecretField label="Verify Token (webhook)" value={f.meta?.verify_token} onChange={updNested("meta", "verify_token")} testid="wa-meta-verify" />
            </div>
          </div>
        )}

        {activeProvider === "interakt" && (
          <div className="rounded border border-border/50 p-3 space-y-3" data-testid="wa-interakt-fields">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Interakt</div>
            <SecretField label="API Key (base64 encoded)" value={f.interakt?.api_key} onChange={updNested("interakt", "api_key")} testid="wa-interakt-key" />
          </div>
        )}

        {activeProvider === "gupshup" && (
          <div className="rounded border border-border/50 p-3 space-y-3" data-testid="wa-gupshup-fields">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Gupshup</div>
            <div className="grid grid-cols-3 gap-3">
              <SecretField label="API Key" value={f.gupshup?.api_key} onChange={updNested("gupshup", "api_key")} testid="wa-gupshup-key" />
              <SecretField label="App Name" value={f.gupshup?.app_name} onChange={updNested("gupshup", "app_name")} testid="wa-gupshup-app" />
              <SecretField label="Source Number" value={f.gupshup?.source_number} onChange={updNested("gupshup", "source_number")} testid="wa-gupshup-src" />
            </div>
          </div>
        )}

        {activeProvider === "twilio" && (
          <div className="rounded border border-border/50 p-3 space-y-3" data-testid="wa-twilio-fields">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Twilio WhatsApp</div>
            <div className="grid grid-cols-3 gap-3">
              <SecretField label="Account SID" value={f.twilio?.account_sid} onChange={updNested("twilio", "account_sid")} testid="wa-twilio-sid" />
              <SecretField label="Auth Token" value={f.twilio?.auth_token} onChange={updNested("twilio", "auth_token")} testid="wa-twilio-token" />
              <SecretField label="From Number (E.164)" value={f.twilio?.from_number} onChange={updNested("twilio", "from_number")} testid="wa-twilio-from" placeholder="whatsapp:+1415..." />
            </div>
          </div>
        )}

        {activeProvider === "msg91" && (
          <div className="rounded border border-border/50 p-3 space-y-3" data-testid="wa-msg91-fields">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">MSG91 WhatsApp</div>
            <div className="grid grid-cols-2 gap-3">
              <SecretField label="Auth Key" value={f.msg91?.auth_key} onChange={updNested("msg91", "auth_key")} testid="wa-msg91-authkey" />
              <SecretField label="Integrated Number" value={f.msg91?.integrated_number} onChange={updNested("msg91", "integrated_number")} testid="wa-msg91-integrated" placeholder="919999999999" />
            </div>
          </div>
        )}

        {/* Test WhatsApp panel */}
        <div className="rounded border border-dashed border-border/60 bg-muted/30 p-3">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Test recipient phone</Label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="10-digit Indian number or +country code"
                className={inpCls}
                data-testid="wa-test-phone"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => testWa.mutate()}
              disabled={testWa.isPending || !testPhone.trim() || !f.enabled}
              data-testid="wa-test-btn"
            >
              <Zap size={14} className="mr-1" />
              {testWa.isPending ? "Sending…" : "Send test message"}
            </Button>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            Save your credentials first · WhatsApp must be enabled · a real message will be dispatched via <b>{providerLabel}</b>.
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => onSave(f)} disabled={saving} data-testid="wa-save-btn"><Save size={14} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TemplateVarsRef() {
  const { data, isLoading } = useQuery({
    queryKey: ["sms-template-vars"],
    queryFn: () => api.get("/integrations/sms-template-vars").then((r) => r.data.templates || []),
  });
  if (isLoading) return null;
  if (!data || data.length === 0) return null;
  return (
    <div className="rounded border border-border/40 bg-background/60 p-2.5" data-testid="sms-template-vars">
      <div className="mb-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">SMS template variables reference</div>
      <div className="text-[11px] text-muted-foreground mb-2">These are the exact placeholder names each SMS template will emit. Either register your MSG91 Flow variables with these names, or map them above.</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="text-muted-foreground">
            <tr className="border-b border-border/40">
              <th className="py-1 pr-3 text-left font-medium">Template key</th>
              <th className="py-1 text-left font-medium">Variables (in order)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((t) => (
              <tr key={t.key} className="border-b border-border/20 last:border-0">
                <td className="py-1 pr-3">{t.key}</td>
                <td className="py-1">{t.placeholders.length === 0 ? <span className="text-muted-foreground italic">(none)</span> : t.placeholders.join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PerTemplateEditor({ perTemplate, onChange, fallbackFlowId, fallbackVarMap }) {
  const { data: refs } = useQuery({
    queryKey: ["sms-template-vars"],
    queryFn: () => api.get("/integrations/sms-template-vars").then((r) => r.data.templates || []),
  });
  const templates = refs || [];

  const updField = (key, field, value) => {
    const current = perTemplate[key] || {};
    onChange({ ...perTemplate, [key]: { ...current, [field]: value } });
  };

  if (templates.length === 0) {
    return <div className="text-xs text-muted-foreground italic py-2">Loading templates…</div>;
  }

  return (
    <div className="space-y-2" data-testid="msg91-per-template">
      {templates.map((t) => {
        const row = perTemplate[t.key] || {};
        const effFlowId = (row.flow_id || "").trim() || fallbackFlowId || "";
        const hasFlow = !!(row.flow_id || "").trim();
        const status = hasFlow ? "configured" : (fallbackFlowId ? "fallback" : "missing");
        const statusStyle = {
          configured: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
          fallback: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
          missing: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
        }[status];
        const statusLabel = { configured: "Configured", fallback: "Fallback", missing: "Not set" }[status];
        return (
          <details
            key={t.key}
            className="group rounded-md border border-border/50 bg-background/60 open:bg-background transition-colors"
            data-testid={`per-template-${t.key}`}
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 hover:bg-muted/30">
              <div className="flex items-center gap-3 min-w-0">
                <ChevronRight size={14} className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
                <div className="min-w-0">
                  <div className="font-mono text-xs font-bold truncate">{t.key}</div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate" title={t.placeholders.join(", ")}>
                    {t.placeholders.length === 0 ? <em>no placeholders</em> : t.placeholders.join(" · ")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {hasFlow && (
                  <code className="hidden sm:inline text-[10px] text-muted-foreground font-mono">{effFlowId.slice(0, 10)}…</code>
                )}
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusStyle}`}>
                  {statusLabel}
                </span>
              </div>
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-3 pb-3 pt-1 border-t border-border/40">
              <div>
                <Label className="text-[11px]">Flow ID</Label>
                <Input
                  value={row.flow_id || ""}
                  onChange={(e) => updField(t.key, "flow_id", e.target.value)}
                  placeholder={fallbackFlowId ? `Fallback: ${fallbackFlowId.slice(0, 10)}…` : "24-char hex from MSG91 dashboard"}
                  className={`${inpCls} font-mono text-xs`}
                  data-testid={`per-template-${t.key}-flow`}
                />
              </div>
              <div>
                <Label className="text-[11px]">Variable map <span className="text-muted-foreground font-normal">(placeholder=msg91_var_name)</span></Label>
                <textarea
                  value={row.variable_map || ""}
                  onChange={(e) => updField(t.key, "variable_map", e.target.value)}
                  placeholder={t.placeholders.map((p) => `${p}=${p}`).join("\n") || "otp=num"}
                  rows={Math.max(2, Math.min(t.placeholders.length, 5))}
                  className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 font-mono text-xs"
                  data-testid={`per-template-${t.key}-varmap`}
                />
                {!row.variable_map && fallbackVarMap && (
                  <div className="mt-1 text-[10px] text-muted-foreground italic">Inherits fallback: <code className="font-mono">{fallbackVarMap.slice(0, 40)}</code></div>
                )}
              </div>
            </div>
          </details>
        );
      })}
    </div>
  );
}

function SMSDispatchLogs() {
  const qc = useQueryClient();
  const [confirmClear, setConfirmClear] = useState(false);
  const [refreshingLive, setRefreshingLive] = useState(false);
  const logs = useQuery({
    queryKey: ["sms-logs"],
    queryFn: () => api.get("/integrations/sms-logs").then((r) => r.data.items || []),
    refetchInterval: 15000,
  });

  const refreshLiveMut = useMutation({
    mutationFn: () => api.get("/integrations/sms-logs?refresh=true").then((r) => r.data),
    onMutate: () => setRefreshingLive(true),
    onSettled: () => setRefreshingLive(false),
    onSuccess: (data) => {
      toast.success(`Live status refreshed for ${(data.items || []).length} rows`);
      qc.setQueryData(["sms-logs"], data.items || []);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "MSG91 status refresh failed"),
  });

  const clearMut = useMutation({
    mutationFn: () => api.delete("/integrations/sms-logs"),
    onSuccess: (r) => {
      toast.success(`Cleared ${r.data.deleted || 0} log entr${r.data.deleted === 1 ? "y" : "ies"}`);
      setConfirmClear(false);
      qc.invalidateQueries({ queryKey: ["sms-logs"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Clear failed"),
  });

  return (
    <Card data-testid="sms-logs-card">
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle>SMS dispatch logs</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">Audit trail of every SMS attempt (test + production). Auto-refreshes every 15s. Last 50 shown.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ["sms-logs"] })}
            data-testid="sms-logs-refresh"
          >
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => refreshLiveMut.mutate()}
            disabled={refreshingLive || !logs.data || logs.data.length === 0}
            data-testid="sms-logs-refresh-msg91"
          >
            {refreshingLive ? "Fetching…" : "Fetch live status (MSG91)"}
          </Button>
          {!confirmClear ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmClear(true)}
              disabled={!logs.data || logs.data.length === 0}
              data-testid="sms-logs-clear-btn"
            >
              Clear logs
            </Button>
          ) : (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => clearMut.mutate()}
                disabled={clearMut.isPending}
                data-testid="sms-logs-clear-confirm"
              >
                {clearMut.isPending ? "Clearing…" : "Confirm delete all"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmClear(false)}
                data-testid="sms-logs-clear-cancel"
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {logs.isLoading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : !logs.data || logs.data.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-6 text-center" data-testid="sms-logs-empty">
            No SMS attempts recorded yet. Fire a test from the SMS tab or trigger an OTP to see entries appear.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="sms-logs-table">
              <thead className="text-muted-foreground">
                <tr className="border-b border-border/40">
                  <th className="py-1.5 pr-2 text-left font-medium">Time</th>
                  <th className="py-1.5 pr-2 text-left font-medium">Provider</th>
                  <th className="py-1.5 pr-2 text-left font-medium">Phone</th>
                  <th className="py-1.5 pr-2 text-left font-medium">Template</th>
                  <th className="py-1.5 pr-2 text-left font-medium">Sent</th>
                  <th className="py-1.5 pr-2 text-left font-medium">Delivery</th>
                  <th className="py-1.5 text-left font-medium">Detail</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {logs.data.map((row, i) => (
                  <tr key={row.created_at || i} className="border-b border-border/20 last:border-0 align-top" data-testid={`sms-log-row-${i}`}>
                    <td className="py-1.5 pr-2 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                    <td className="py-1.5 pr-2 uppercase">{row.provider}</td>
                    <td className="py-1.5 pr-2">{row.phone_masked}</td>
                    <td className="py-1.5 pr-2">{row.template_key || <span className="text-muted-foreground">—</span>}</td>
                    <td className={`py-1.5 pr-2 font-bold ${row.ok ? "text-emerald-600" : "text-rose-600"}`}>
                      {row.ok ? "OK" : "FAIL"}
                      {row.response_status ? <span className="ml-1 text-muted-foreground font-normal">({row.response_status})</span> : null}
                    </td>
                    <td className="py-1.5 pr-2">
                      {row.delivery_status ? (
                        <span className={
                          "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase " +
                          ((row.delivery_status || "").toLowerCase().includes("deliver")
                            ? "bg-emerald-500/10 text-emerald-600"
                            : (row.delivery_status || "").toLowerCase().includes("fail") || (row.delivery_status || "").toLowerCase().includes("reject")
                              ? "bg-rose-500/10 text-rose-600"
                              : (row.delivery_status || "").toLowerCase().includes("dnd")
                                ? "bg-amber-500/10 text-amber-600"
                                : "bg-muted text-muted-foreground")
                        }>
                          {row.delivery_status}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic text-[10px]">pending</span>
                      )}
                    </td>
                    <td className="py-1.5 text-muted-foreground max-w-[380px]">
                      <div className="truncate" title={row.message}>{row.message}</div>
                      {row.sent_vars && Object.keys(row.sent_vars).length > 0 && (
                        <div className="mt-0.5 text-[10px] text-muted-foreground/80" data-testid={`sms-log-vars-${i}`}>
                          sent: {Object.entries(row.sent_vars).map(([k, v]) => `${k}=${v}`).join(", ")}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SMSCard({ cfg, onSave, saving }) {
  const [f, setF] = useState({ ...cfg });
  const [testPhone, setTestPhone] = useState("");
  const [testTemplate, setTestTemplate] = useState("staff_otp_sms");
  const upd = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const updN = (grp, k) => (e) => setF({ ...f, [grp]: { ...f[grp], [k]: e?.target ? e.target.value : e } });

  const activeProvider = f.active_provider || "msg91";

  const qc = useQueryClient();
  const availableTemplates = useQuery({
    queryKey: ["sms-template-vars"],
    queryFn: () => api.get("/integrations/sms-template-vars").then((r) => r.data.templates || []),
  });

  const testSms = useMutation({
    mutationFn: () => api.post("/integrations/test", { group: "sms", phone: testPhone, template_key: testTemplate }),
    onSuccess: (r) => {
      if (r.data.ok) toast.success(r.data.message); else toast.error(r.data.message);
      qc.invalidateQueries({ queryKey: ["sms-logs"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Test failed"),
  });

  return (
    <Card data-testid="sms-integration-card">
      <CardHeader className="flex-row items-start justify-between border-b border-border/40 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare size={18} className="text-primary" />
            SMS Gateway
          </CardTitle>
          <div className="text-xs text-muted-foreground mt-1">Transactional OTP, order updates &amp; delivery notifications via MSG91 or Twilio</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Status</div>
            <div className={`text-xs font-bold ${f.enabled ? "text-emerald-600" : "text-muted-foreground"}`}>{f.enabled ? "● Live" : "○ Disabled"}</div>
          </div>
          <Switch checked={!!f.enabled} onCheckedChange={upd("enabled")} data-testid="sms-enabled" />
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-5">

        {/* SECTION 1: Provider selection */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold">STEP 1</div>
            <div className="text-sm font-semibold">Choose your SMS provider</div>
          </div>
          <div className="max-w-sm">
            <Select value={activeProvider} onValueChange={upd("active_provider")}>
              <SelectTrigger data-testid="sms-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="msg91">MSG91 (India, DLT-compliant)</SelectItem>
                <SelectItem value="twilio">Twilio SMS (Global)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* SECTION 2: Provider credentials */}
        {activeProvider === "msg91" && (
          <section data-testid="sms-msg91-fields">
            <div className="flex items-center gap-2 mb-3">
              <div className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold">STEP 2</div>
              <div className="text-sm font-semibold">MSG91 credentials</div>
              <span className="text-[10px] text-muted-foreground">v5 Flow API · encrypted at rest</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SecretField label="Auth Key" value={f.msg91?.auth_key} onChange={updN("msg91", "auth_key")} testid="sms-msg91-authkey" placeholder="From MSG91 dashboard → API" />
              <SecretField label="Sender ID (6 chars, DLT-approved)" value={f.msg91?.sender_id} onChange={updN("msg91", "sender_id")} testid="sms-msg91-sender" placeholder="e.g. FuelEx" />
              <SecretField label="Fallback Flow ID" value={f.msg91?.template_id} onChange={updN("msg91", "template_id")} testid="sms-msg91-template" placeholder="Used only for staff_otp_sms" />
            </div>
            <details className="mt-3 rounded-md border border-border/40 bg-muted/20 open:bg-muted/30">
              <summary className="cursor-pointer list-none px-3 py-2 text-[11px] font-semibold text-muted-foreground hover:text-foreground">
                Advanced · Global fallback variable map
              </summary>
              <div className="px-3 pb-3">
                <textarea
                  value={f.msg91?.variable_map || ""}
                  onChange={updN("msg91", "variable_map")}
                  placeholder={"otp=num\nbrand=var1"}
                  rows={3}
                  className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 font-mono text-xs"
                  data-testid="sms-msg91-varmap"
                />
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Applied to templates without their own map. Format: <code className="rounded bg-black/10 px-1">placeholder=msg91_var_name</code> per line. Best practice: leave empty and configure per-template rows below.
                </div>
              </div>
            </details>
          </section>
        )}

        {activeProvider === "twilio" && (
          <section data-testid="sms-twilio-fields">
            <div className="flex items-center gap-2 mb-3">
              <div className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold">STEP 2</div>
              <div className="text-sm font-semibold">Twilio credentials</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SecretField label="Account SID" value={f.twilio?.account_sid} onChange={updN("twilio", "account_sid")} testid="sms-twilio-sid" />
              <SecretField label="Auth Token" value={f.twilio?.auth_token} onChange={updN("twilio", "auth_token")} testid="sms-twilio-token" />
              <SecretField label="From Number (E.164)" value={f.twilio?.from_number} onChange={updN("twilio", "from_number")} testid="sms-twilio-from" placeholder="+1415..." />
            </div>
          </section>
        )}

        {/* SECTION 3: Per-template mappings (only for MSG91) */}
        {activeProvider === "msg91" && (
          <section>
            <div className="flex items-center gap-2 mb-1">
              <div className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold">STEP 3</div>
              <div className="text-sm font-semibold">Per-template Flow IDs</div>
            </div>
            <div className="text-[11px] text-muted-foreground mb-3">
              Each transactional SMS type needs its own DLT-approved template AND a matching MSG91 Flow. Click a row to configure.
            </div>
            <PerTemplateEditor
              perTemplate={f.msg91?.per_template || {}}
              onChange={(pt) => setF({ ...f, msg91: { ...f.msg91, per_template: pt } })}
              fallbackFlowId={f.msg91?.template_id}
              fallbackVarMap={f.msg91?.variable_map}
            />
          </section>
        )}

        {/* SECTION 4: Test dispatch */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold">STEP 4</div>
            <div className="text-sm font-semibold">Send a test SMS</div>
            <span className="text-[10px] text-muted-foreground">Fires a real message via {activeProvider === "msg91" ? "MSG91" : "Twilio"}</span>
          </div>
          <div className="rounded-md border border-dashed border-border/70 bg-gradient-to-b from-muted/20 to-transparent p-4">
            <div className="grid grid-cols-1 md:grid-cols-[minmax(200px,1fr)_minmax(220px,1fr)_auto] gap-3 items-end">
              <div>
                <Label>Template</Label>
                <Select value={testTemplate} onValueChange={setTestTemplate}>
                  <SelectTrigger data-testid="sms-test-template"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                  <SelectContent>
                    {(availableTemplates.data || []).map((t) => (
                      <SelectItem key={t.key} value={t.key} data-testid={`sms-test-template-option-${t.key}`}>
                        {t.key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Test recipient phone</Label>
                <Input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="10-digit Indian or E.164 (+91…)"
                  className={inpCls}
                  data-testid="sms-test-phone"
                />
              </div>
              <Button
                onClick={() => testSms.mutate()}
                disabled={testSms.isPending || !testPhone.trim() || !f.enabled || !testTemplate}
                data-testid="sms-test-btn"
                className="min-w-[140px]"
              >
                <Zap size={14} className="mr-1" />
                {testSms.isPending ? "Sending…" : "Send test SMS"}
              </Button>
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5">
              <Info size={12} className="mt-0.5 shrink-0" />
              <span>Save credentials first. Sample values are used for placeholders (otp=1234, order_code=ORD/25-26/0007, etc.). Real dispatch appears in <b>SMS Logs</b> tab.</span>
            </div>
          </div>
        </section>

        {/* Sticky-feel save bar */}
        <div className="sticky bottom-0 -mx-6 -mb-6 mt-4 border-t border-border/60 bg-card/95 backdrop-blur px-6 py-3 flex items-center justify-between">
          <div className="text-[11px] text-muted-foreground">
            {f.enabled ? "✓ Integration is live — changes take effect immediately after save." : "Toggle Enable to activate the integration."}
          </div>
          <Button onClick={() => onSave(f)} disabled={saving} data-testid="sms-save-btn">
            <Save size={14} className="mr-1" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmailCard({ cfg, onSave, saving }) {
  const [f, setF] = useState({ ...cfg });
  const [testTo, setTestTo] = useState("");
  const upd = (k) => (e) => setF({ ...f, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });

  const qc = useQueryClient();
  const emailLogs = useQuery({
    queryKey: ["email-logs"],
    queryFn: () => api.get("/integrations/email-logs").then((r) => r.data.items || []),
    refetchInterval: 15000,
  });

  const testMail = useMutation({
    mutationFn: () => api.post("/integrations/test-email", { to: testTo }),
    onSuccess: (r) => {
      if (r.data.ok) toast.success(r.data.message); else toast.error(r.data.message);
      qc.invalidateQueries({ queryKey: ["email-logs"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Test failed"),
  });

  const [confirmClear, setConfirmClear] = useState(false);
  const clearMut = useMutation({
    mutationFn: () => api.delete("/integrations/email-logs"),
    onSuccess: (r) => {
      toast.success(`Cleared ${r.data.deleted || 0} log entr${r.data.deleted === 1 ? "y" : "ies"}`);
      setConfirmClear(false);
      qc.invalidateQueries({ queryKey: ["email-logs"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Clear failed"),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div><CardTitle>Email · SMTP</CardTitle><div className="text-xs text-muted-foreground mt-1">Password resets, invoice PDFs, receipts, alerts</div></div>
        <div className="flex items-center gap-2"><span className="text-xs">Enable</span><Switch checked={!!f.enabled} onCheckedChange={upd("enabled")} data-testid="email-enabled" /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <SecretField label="SMTP host" value={f.smtp_host} onChange={upd("smtp_host")} placeholder="smtp.sendgrid.net" testid="email-smtp-host" />
          <div><Label>SMTP port</Label><Input type="number" value={f.smtp_port || 587} onChange={upd("smtp_port")} className={inpCls} data-testid="email-smtp-port" /></div>
          <SecretField label="SMTP user" value={f.smtp_user} onChange={upd("smtp_user")} placeholder="apikey / your-username" testid="email-smtp-user" />
          <SecretField label="SMTP password" value={f.smtp_password} onChange={upd("smtp_password")} testid="email-smtp-password" />
          <SecretField label="From email" value={f.from_email} onChange={upd("from_email")} placeholder="billing@fuelexpress.co.in" testid="email-from" />
          <SecretField label="From name" value={f.from_name} onChange={upd("from_name")} placeholder="FuelExpress" testid="email-from-name" />
        </div>

        <div className="rounded border border-dashed border-border/60 bg-muted/30 p-3">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Test recipient email</Label>
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@example.com"
                className={inpCls}
                data-testid="email-test-to"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => testMail.mutate()}
              disabled={testMail.isPending || !testTo.trim() || !f.enabled}
              data-testid="email-test-btn"
            >
              <Zap size={14} className="mr-1" />
              {testMail.isPending ? "Sending…" : "Send test email"}
            </Button>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            Save credentials first · Email must be enabled · a real message will be dispatched via SMTP.
          </div>
        </div>

        <div className="rounded border border-border/50 bg-background/40 p-3" data-testid="email-logs-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Recent email dispatches (last 50)</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => qc.invalidateQueries({ queryKey: ["email-logs"] })}
                className="text-[11px] text-muted-foreground underline hover:text-foreground"
                data-testid="email-logs-refresh"
              >
                Refresh
              </button>
              {!confirmClear ? (
                <button
                  type="button"
                  onClick={() => setConfirmClear(true)}
                  disabled={!emailLogs.data || emailLogs.data.length === 0}
                  className="text-[11px] text-muted-foreground underline hover:text-foreground disabled:opacity-40"
                  data-testid="email-logs-clear"
                >
                  Clear
                </button>
              ) : (
                <>
                  <button type="button" onClick={() => clearMut.mutate()} disabled={clearMut.isPending} className="text-[11px] font-bold text-rose-600 underline" data-testid="email-logs-clear-confirm">
                    {clearMut.isPending ? "Clearing…" : "Confirm delete all"}
                  </button>
                  <button type="button" onClick={() => setConfirmClear(false)} className="text-[11px] text-muted-foreground underline" data-testid="email-logs-clear-cancel">
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
          {emailLogs.isLoading ? (
            <div className="text-xs text-muted-foreground">Loading…</div>
          ) : !emailLogs.data || emailLogs.data.length === 0 ? (
            <div className="text-xs text-muted-foreground italic" data-testid="email-logs-empty">No email attempts yet. Save your SMTP config, enable it, then fire a test above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="email-logs-table">
                <thead className="text-muted-foreground">
                  <tr className="border-b border-border/40">
                    <th className="py-1 pr-2 text-left font-medium">Time</th>
                    <th className="py-1 pr-2 text-left font-medium">To</th>
                    <th className="py-1 pr-2 text-left font-medium">Subject</th>
                    <th className="py-1 pr-2 text-left font-medium">Status</th>
                    <th className="py-1 text-left font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {emailLogs.data.slice(0, 20).map((row, i) => (
                    <tr key={row.created_at || i} className="border-b border-border/20 last:border-0" data-testid={`email-log-row-${i}`}>
                      <td className="py-1 pr-2 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                      <td className="py-1 pr-2">{row.to}</td>
                      <td className="py-1 pr-2 truncate max-w-[240px]" title={row.subject}>{row.subject}</td>
                      <td className={`py-1 pr-2 font-bold ${row.ok ? "text-emerald-600" : "text-rose-600"}`}>
                        {row.ok ? "OK" : "FAIL"}
                      </td>
                      <td className="py-1 text-muted-foreground truncate max-w-[280px]" title={row.message}>{row.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={() => onSave(f)} disabled={saving} data-testid="email-save-btn">
            <Save size={14} className="mr-1" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MapsCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-map-settings"],
    queryFn: () => api.get("/admin/map-settings").then((r) => r.data),
  });
  const [form, setForm] = useState(null);

  React.useEffect(() => {
    if (data && !form) setForm({
      provider: data.provider,
      google_api_key: data.google_api_key || "",
      google_map_type: data.google_map_type || "roadmap",
      mapbox_access_token: data.mapbox_access_token || "",
      mapbox_style_id: data.mapbox_style_id || "mapbox/streets-v12",
      here_api_key: data.here_api_key || "",
      here_style: data.here_style || "explore.day",
    });
  }, [data, form]);

  const save = useMutation({
    mutationFn: (payload) => api.patch("/admin/map-settings", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Map provider saved — refresh any open map to see it live");
      invalidateMapProviderCache();
      qc.invalidateQueries({ queryKey: ["admin-map-settings"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  if (isLoading || !data || !form) return <div className="text-sm text-muted-foreground">Loading map settings…</div>;

  const provider = form.provider;
  const providers = data.supported_providers || [];

  return (
    <Card data-testid="maps-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapIcon size={16} className="text-primary" />
          Map provider
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground flex gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>
            Choose the map tile source used across the app — <b>Live Tracking</b>, <b>Ongoing Trips</b>, <b>Route Optimizer</b>, and the customer <b>Order Location Picker</b>. Changing this is a global switch. Bring your own key for production traffic on paid providers.
          </div>
        </div>

        <div>
          <Label>Active provider</Label>
          <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
            <SelectTrigger data-testid="maps-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`maps-provider-${p.id}`}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {providers.find((p) => p.id === provider)?.notes ? (
            <p className="mt-1 text-xs text-muted-foreground">{providers.find((p) => p.id === provider).notes}</p>
          ) : null}
        </div>

        {provider === "google" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Google Maps API key <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input
                value={form.google_api_key}
                onChange={(e) => setForm({ ...form, google_api_key: e.target.value })}
                placeholder="AIza…"
                data-testid="maps-google-key"
                className={inpCls}
              />
              <p className="mt-1 text-xs text-muted-foreground">Create at console.cloud.google.com → Credentials. Enable Maps JavaScript API + billing for production.</p>
            </div>
            <div>
              <Label>Map style</Label>
              <Select value={form.google_map_type} onValueChange={(v) => setForm({ ...form, google_map_type: v })}>
                <SelectTrigger data-testid="maps-google-style"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="roadmap">Roadmap</SelectItem>
                  <SelectItem value="satellite">Satellite</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="terrain">Terrain</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {provider === "mapbox" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Mapbox access token</Label>
              <Input
                value={form.mapbox_access_token}
                onChange={(e) => setForm({ ...form, mapbox_access_token: e.target.value })}
                placeholder="pk.eyJ…"
                data-testid="maps-mapbox-token"
                className={inpCls}
              />
              <p className="mt-1 text-xs text-muted-foreground">Grab a public token from account.mapbox.com → Access tokens.</p>
            </div>
            <div>
              <Label>Style ID</Label>
              <Input
                value={form.mapbox_style_id}
                onChange={(e) => setForm({ ...form, mapbox_style_id: e.target.value })}
                placeholder="mapbox/streets-v12"
                data-testid="maps-mapbox-style"
                className={inpCls}
              />
              <p className="mt-1 text-xs text-muted-foreground">e.g. mapbox/streets-v12, mapbox/satellite-v9, mapbox/dark-v11</p>
            </div>
          </div>
        )}

        {provider === "here" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>HERE API key</Label>
              <Input
                value={form.here_api_key}
                onChange={(e) => setForm({ ...form, here_api_key: e.target.value })}
                placeholder="Your HERE apiKey"
                data-testid="maps-here-key"
                className={inpCls}
              />
              <p className="mt-1 text-xs text-muted-foreground">Get one from platform.here.com → Projects → REST APIs.</p>
            </div>
            <div>
              <Label>Style</Label>
              <Select value={form.here_style} onValueChange={(v) => setForm({ ...form, here_style: v })}>
                <SelectTrigger data-testid="maps-here-style"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="explore.day">Explore · Day</SelectItem>
                  <SelectItem value="explore.night">Explore · Night</SelectItem>
                  <SelectItem value="explore.satellite.day">Satellite · Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {provider === "osm" && (
          <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            OpenStreetMap tiles are served free — no key required. Attribution is added automatically to every map.
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={() => save.mutate(form)}
            disabled={save.isPending}
            data-testid="maps-save-btn"
          >
            <Save size={14} className="mr-1" />
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

